import { useState, useEffect, useRef } from 'react';
import {
  Mail,
  Send,
  ArrowUpRight,
  ArrowDownLeft,
  Loader2,
  ChevronLeft,
  Reply,
  ChevronDown,
  ChevronUp,
  Trash2,
} from 'lucide-react';
import { ScrollArea, useAuth, Button, Label } from '@aaas/shared';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  useEmailThreadQuery,
  useReplyToThreadMutation,
} from '../../hooks/useEmailQueries';
import { useEmailSignatureListQuery } from '../../hooks/useEmailSignatureQueries';
import { format } from 'date-fns';
import { sanitizeHtml } from '../../utils/sanitize-html';
import { TiptapEmailEditor } from '../../components/TiptapEmailEditor';
import type { EmailMessage, EmailRecipient } from '../../types';

export function EmailDetailPage() {
  const { t } = useTranslation('ats');
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const { currentWorkspace, idToken } = useAuth();
  const queryClient = useQueryClient();

  // Invalidate cache on mount / threadId change so we always get fresh data
  useEffect(() => {
    if (threadId) {
      queryClient.invalidateQueries({ queryKey: ['email-thread', threadId] });
    }
  }, [threadId, queryClient]);

  const { data: threadDetail, isLoading } = useEmailThreadQuery({
    threadId,
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    enabled: !!threadId && !!currentWorkspace?.id && !!idToken,
  });

  const thread = threadDetail?.data;
  const messages = thread?.messages || [];
  const subject = thread?.subject || t('email.detail.fallbackSubject');
  const replyTo = deriveReplyRecipient(messages);

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] bg-white rounded-lg border overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b flex items-center gap-3 shrink-0">
        <button
          type="button"
          onClick={() => navigate('/ats/emails')}
          className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div className="flex-1 min-w-0">
          {isLoading ? (
            <div className="h-6 w-48 bg-gray-100 rounded animate-pulse" />
          ) : (
            <>
              <h2 className="text-lg font-semibold text-gray-900 truncate">
                {subject}
              </h2>
              <div className="flex items-center gap-2 mt-0.5 text-sm text-gray-500">
                {thread?.candidate_name && <span>{thread.candidate_name}</span>}
                {thread?.job_code && (
                  <span className="inline-flex items-center bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">
                    {thread.job_code}
                  </span>
                )}
                {messages.length > 0 && (
                  <span className="text-gray-400">
                    {t('email.detail.messageCount', { count: messages.length })}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1">
        <div className="max-w-3xl mx-auto p-6 space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Mail className="h-10 w-10 mb-3" />
              <p className="text-sm">{t('email.detail.empty.noMessages')}</p>
            </div>
          ) : (
            <>
              {messages.map((msg, index) => (
                <EmailMessageCard
                  key={msg.id}
                  message={msg}
                  isLast={index === messages.length - 1}
                />
              ))}
              {threadId && (
                <InlineReplyComposer threadId={threadId} replyTo={replyTo} />
              )}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

/* ─── Helpers ─── */

function deriveReplyRecipient(
  messages: EmailMessage[],
): EmailRecipient | null {
  if (messages.length === 0) return null;
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.direction === 'inbound') {
      return { email: msg.from_email };
    }
  }
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg.direction === 'outbound' && msg.to_emails?.length > 0) {
      return msg.to_emails[0];
    }
  }
  return null;
}

/* ─── Email Message Card ─── */

function EmailMessageCard({
  message,
  isLast,
}: {
  message: EmailMessage;
  isLast: boolean;
}) {
  const { t } = useTranslation('ats');
  const isOutbound = message.direction === 'outbound';
  const [isExpanded, setIsExpanded] = useState(isLast);

  const toList = message.to_emails
    ?.map((r) => r.name || r.email)
    .join(', ');

  return (
    <div
      className={`border rounded-lg overflow-hidden ${
        isExpanded
          ? isOutbound
            ? 'border-l-2 border-l-orange-400'
            : 'border-l-2 border-l-blue-400'
          : ''
      }`}
    >
      {/* Message header - always visible */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors ${
          isExpanded ? 'border-b' : ''
        }`}
      >
        {/* Avatar */}
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium shrink-0 ${
            isOutbound
              ? 'bg-orange-100 text-orange-700'
              : 'bg-blue-100 text-blue-700'
          }`}
        >
          {isOutbound ? (
            <Send className="h-4 w-4" />
          ) : (
            message.from_email.charAt(0).toUpperCase()
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 truncate">
              {isOutbound ? t('email.detail.sender.me') : message.from_email}
            </span>
            {isOutbound && (
              <ArrowUpRight className="h-3 w-3 text-orange-500 shrink-0" />
            )}
            {!isOutbound && (
              <ArrowDownLeft className="h-3 w-3 text-blue-500 shrink-0" />
            )}
            {message.status === 'failed' && (
              <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded shrink-0">
                {t('email.detail.badge.failed')}
              </span>
            )}
          </div>
          {!isExpanded && (
            <p className="text-xs text-gray-500 truncate mt-0.5">
              {message.body_text
                ? message.body_text.substring(0, 100)
                : t('email.detail.empty.noContent')}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-gray-400">
            {format(new Date(message.sent_at ?? message.created_at), 'dd/MM/yyyy HH:mm')}
          </span>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </button>

      {/* Message body - collapsible */}
      {isExpanded && (
        <div className="px-4 py-3">
          {/* Recipient info */}
          <div className="text-xs text-gray-500 mb-3 space-y-0.5">
            <p>
              <span className="text-gray-400">{t('email.detail.labels.from')}</span>{' '}
              {message.from_email}
            </p>
            {toList && (
              <p>
                <span className="text-gray-400">{t('email.detail.labels.to')}</span> {toList}
              </p>
            )}
            {message.cc_emails?.length > 0 && (
              <p>
                <span className="text-gray-400">{t('email.detail.labels.cc')}</span>{' '}
                {message.cc_emails.map((r) => r.name || r.email).join(', ')}
              </p>
            )}
          </div>

          {/* Email body */}
          {message.body_html ? (
            <div
              className="prose prose-sm max-w-none text-gray-800"
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(message.body_html),
              }}
            />
          ) : message.body_text ? (
            <p className="text-sm whitespace-pre-wrap text-gray-800">
              {message.body_text}
            </p>
          ) : (
            <p className="text-sm text-gray-400 italic">{t('email.detail.empty.noContent')}</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Inline Reply Composer (Gmail-style) ─── */

function InlineReplyComposer({
  threadId,
  replyTo,
}: {
  threadId: string;
  replyTo: EmailRecipient | null;
}) {
  const { t } = useTranslation('ats');
  const [isExpanded, setIsExpanded] = useState(false);
  const [replyHtml, setReplyHtml] = useState('');
  const [selectedSignatureId, setSelectedSignatureId] = useState<string>('');
  const replyMutation = useReplyToThreadMutation();
  const containerRef = useRef<HTMLElement>(null);

  const { currentWorkspace, idToken } = useAuth();
  const { data: signatureResponse } = useEmailSignatureListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    enabled: isExpanded,
    size: 100,
  });
  const signatures = signatureResponse?.data?.items || [];

  useEffect(() => {
    if (isExpanded) {
      containerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, [isExpanded]);

  const handleSend = () => {
    const html = replyHtml.trim();
    if (!html) return;

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const bodyText = tempDiv.textContent || tempDiv.innerText || '';

    replyMutation.mutate(
      {
        thread_id: threadId,
        body_html: html,
        body_text: bodyText,
        signature_id: selectedSignatureId || undefined,
      },
      {
        onSuccess: () => {
          setReplyHtml('');
          setSelectedSignatureId('');
          setIsExpanded(false);
        },
      },
    );
  };

  const handleDiscard = () => {
    setReplyHtml('');
    setSelectedSignatureId('');
    setIsExpanded(false);
  };

  if (!isExpanded) {
    return (
      <button
        ref={containerRef as React.Ref<HTMLButtonElement>}
        type="button"
        onClick={() => setIsExpanded(true)}
        className="w-full text-left border rounded-lg px-4 py-3 flex items-center gap-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
      >
        <Reply className="h-4 w-4 text-gray-500" />
        {t('email.detail.actions.reply')}
      </button>
    );
  }

  const recipientLabel = replyTo
    ? replyTo.name
      ? `${replyTo.name} <${replyTo.email}>`
      : replyTo.email
    : t('email.detail.composer.recipientFallback');

  return (
    <div
      ref={containerRef as React.Ref<HTMLDivElement>}
      className="border rounded-lg overflow-hidden bg-white shadow-sm"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b flex items-center gap-3 text-sm">
        <Reply className="h-4 w-4 text-gray-500 shrink-0" />
        <span className="text-gray-400 shrink-0">{t('email.detail.labels.to')}</span>
        <span className="text-gray-800 truncate">{recipientLabel}</span>
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-3">
        <TiptapEmailEditor
          value={replyHtml}
          onChange={setReplyHtml}
          placeholder={t('email.detail.composer.replyPlaceholder')}
        />

        <div className="space-y-1">
          <Label htmlFor="reply-signature" className="text-xs text-gray-500">
            {t('email.detail.composer.signatureLabel')}
          </Label>
          <select
            id="reply-signature"
            value={selectedSignatureId}
            onChange={(e) => setSelectedSignatureId(e.target.value)}
            disabled={replyMutation.isPending}
            className="w-full h-9 rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="">{t('email.detail.composer.signatureNone')}</option>
            {signatures.map((sig) => (
              <option key={sig.id} value={sig.id}>
                {sig.name}
                {sig.is_default ? t('email.detail.composer.signatureDefaultSuffix') : ''}
              </option>
            ))}
          </select>
        </div>

        {replyMutation.isError && (
          <p className="text-xs text-red-600">
            {replyMutation.error?.message || t('email.detail.composer.sendFailed')}
          </p>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t flex items-center justify-between">
        <Button
          onClick={handleSend}
          disabled={!replyHtml.trim() || replyMutation.isPending}
          className="bg-orange-600 hover:bg-orange-700 text-white"
        >
          {replyMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin mr-1" />
          ) : (
            <Send className="h-4 w-4 mr-1" />
          )}
          {t('email.detail.actions.send')}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleDiscard}
          disabled={replyMutation.isPending}
          aria-label={t('email.detail.actions.discard')}
          className="text-gray-500 hover:text-red-600"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
